const CONSENT_STORAGE_KEY = "elra_image_cache_consent";
const CONSENT_COOKIE_KEY = "elra_image_cache_consent";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
export const IMAGE_CACHE_PREFERENCES_QUERY_KEY = "manage-cache";

export type ImageCacheConsentStatus = "accepted" | "declined" | "unset";

const readCookieValue = (name: string) => {
  if (typeof document === "undefined") return "";

  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
};

const writeCookieValue = (name: string, value: string, maxAgeSeconds: number) => {
  if (typeof document === "undefined") return;

  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax`;
};

export const getImageCacheConsentStatus = (): ImageCacheConsentStatus => {
  if (typeof window === "undefined") return "unset";

  const storageValue = String(window.localStorage.getItem(CONSENT_STORAGE_KEY) || "").trim();
  if (storageValue === "accepted" || storageValue === "declined") return storageValue;

  const cookieValue = String(readCookieValue(CONSENT_COOKIE_KEY) || "").trim();
  if (cookieValue === "accepted" || cookieValue === "declined") return cookieValue;

  return "unset";
};

export const setImageCacheConsentStatus = (status: Exclude<ImageCacheConsentStatus, "unset">) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
  writeCookieValue(CONSENT_COOKIE_KEY, status, ONE_YEAR_SECONDS);
};

let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

export const registerImageCacheServiceWorker = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;

  if (swRegistrationPromise) return swRegistrationPromise;

  swRegistrationPromise = navigator.serviceWorker
    .register("/sw-image-cache.js", { scope: "/" })
    .catch(() => null);

  return swRegistrationPromise;
};

export const unregisterImageCacheServiceWorkers = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations
      .filter((registration) => registration.active?.scriptURL.includes("sw-image-cache.js"))
      .map((registration) => registration.unregister()),
  );
  swRegistrationPromise = null;
};

export const clearImageCacheStorage = async () => {
  if (typeof window === "undefined" || !("caches" in window)) return;

  const cacheKeys = await caches.keys();
  await Promise.all(
    cacheKeys
      .filter((key) => key.startsWith("elra-image-cache-"))
      .map((key) => caches.delete(key)),
  );
};
